import { useState } from "react";
import { FileText, ChevronDown } from "lucide-react";
import { fmtDate, niceName } from "@/lib/format";
import { AiCorrect, DocImage, isIdDoc } from "@/components/AiCorrect";

const AI_DOC_LABEL = {
  certidao_permanente: "Certidão permanente", caderneta_predial: "Caderneta predial",
  certificado_energetico: "Certificado energético", licenca_utilizacao: "Licença de utilização",
  documento_identificacao: "Documento de identificação", cpcv: "CPCV", outro: "Outro documento",
};
const HIDDEN = new Set(["codigo_validacao", "modelo1_data", "avaliacao_data"]);
const AREA_HIDDEN = new Set(["area_total", "areas", "area_total_terreno", "area_implantacao",
  "area_bruta_construcao", "area_bruta_privativa", "area_bruta_dependente", "area_coberta", "area_descoberta"]);
const PROP_TYPES = ["caderneta_predial", "certidao_permanente"];
const MERGED = [
  { label: "Descrição predial", keys: ["numero_descricao", "descricao_conservatoria"] },
];
const DATES = ["data_emissao", "data_validade", "data_licenca", "data_anuncio", "data_ficha", "data_pagamento", "data_assinatura", "data_limite_escritura", "data_aquisicao", "data_nascimento", "data_cpcv", "ce_validade", "licenca_data"];
const r = (label, keys, first = false) => ({ label, keys, first });
const KEY_ROWS = {
  caderneta_predial: [r("Artigo e localização", ["artigo_matricial", "fracao", "freguesia", "concelho"]), r("Titulares", ["titulares"]),
    r("Terreno", ["_terreno"]), r("Construção", ["_construcao"]), r("Ano de inscrição", ["ano_inscricao"]), r("Emitida a", ["data_emissao"])],
  certidao_permanente: [r("Descrição predial", ["numero_descricao", "descricao_conservatoria", "conservatoria"]), r("Proprietários", ["titulares"]),
    r("Terreno", ["_terreno"]), r("Ónus e hipotecas", ["onus_hipotecas"]), r("Emissão e validade", ["data_emissao", "data_validade"])],
  cpcv: [r("Vendedores", ["vendedores"]), r("Compradores", ["compradores"]), r("Imóvel", ["artigo_matricial", "morada"]),
    r("Preço", ["preco"]), r("Sinal", ["sinal"])],
  _default: [r("Número", ["numero_documento", "numero_licenca", "numero_anuncio", "numero_descricao", "nipc"], true),
    r("Data", ["data_emissao", "data_licenca", "data_anuncio", "data_ficha", "data_pagamento", "data_assinatura"], true),
    r("Validade", ["data_validade"]), r("Refere-se a", ["nome", "titulares", "contribuinte", "firma", "vendedores", "morada"], true)],
};

export const m2 = (x) => `${x.toFixed(2).replace(".", ",").replace(/,00$/, "")} m²`;
const tidy = (v) => String(v).replace(/(\d+)[.,](\d{2})\d{2,}(?=\s*m)/g, "$1,$2").replace(/\bm2\b/g, "m²");
const show = (f) => (DATES.includes(f.key) ? fmtDate(f.value) : typeof f.value === "string" ? tidy(f.value) : f.value);
const distinct = (fs) => [...new Map(fs.map((f) => [String(show(f)).trim().toLowerCase(), f])).values()];

const AREA_PATS = [["implantacao", /implanta/], ["bruta_construcao", /bruta\s+de\s+constru/], ["bruta_privativa", /privativa/],
  ["dependente", /dependente/], ["descoberta", /descobert/], ["coberta", /cobert/], ["terreno", /terreno|total/]];
const AREA_FIELDS = { terreno: ["area_total_terreno", "area_total"], implantacao: ["area_implantacao"], bruta_construcao: ["area_bruta_construcao"],
  bruta_privativa: ["area_bruta_privativa"], dependente: ["area_bruta_dependente"], coberta: ["area_coberta"], descoberta: ["area_descoberta"] };
const num = (v) => { const n = parseFloat(String(v ?? "").replace(",", ".").replace(/[^\d.]/g, "")); return n > 0 ? n : null; };

function areaFields(byKey) {
  const a = {};
  Object.entries(AREA_FIELDS).forEach(([k, keys]) => { const f = keys.map((x) => num(byKey[x]?.value)).find(Boolean); if (f) a[k] = f; });
  String(byKey.areas?.value || "").split(/[;\n·]/).forEach((seg) => {
    const s = seg.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const m = s.match(/(\d+(?:[.,]\d+)?)/);
    const k = m && AREA_PATS.find(([, re]) => re.test(s))?.[0];
    if (k && !a[k]) a[k] = parseFloat(m[1].replace(",", "."));
  });
  const out = {};
  if (a.terreno) {
    const sub = [a.coberta && `coberta ${m2(a.coberta)}`, a.descoberta && `descoberta ${m2(a.descoberta)}`].filter(Boolean).join(" · ");
    out._terreno = { key: "_terreno", value: `${m2(a.terreno)}${sub ? ` (${sub})` : ""}`, confidence: byKey.area_total?.confidence || "alto" };
  }
  const c = [["implantacao", "implantação"], ["bruta_construcao", "bruta"], ["bruta_privativa", "privativa"], ["dependente", "dependente"]]
    .filter(([k]) => a[k]).map(([k, l]) => `${l} ${m2(a[k])}`).join(" · ");
  if (c) out._construcao = { key: "_construcao", value: c, confidence: byKey.areas?.confidence || "alto" };
  return out;
}

export function buildRows(ai) {
  const byKey = Object.fromEntries((ai.fields || []).filter((f) => f.value !== "" && f.value != null).map((f) => [f.key, f]));
  const prop = PROP_TYPES.includes(ai.doc_type);
  if (prop) Object.assign(byKey, areaFields(byKey));
  const used = new Set();
  const take = (row) => {
    let fs = row.keys.map((k) => byKey[k]).filter(Boolean);
    if (row.first) fs = fs.slice(0, 1);
    row.keys.forEach((k) => used.add(k));
    return fs.length ? { label: row.label, fields: distinct(fs) } : null;
  };
  const key = (KEY_ROWS[ai.doc_type] || KEY_ROWS._default).map(take).filter(Boolean).slice(0, 6);
  const rest = [];
  MERGED.forEach((m) => { if (m.keys.some((k) => byKey[k] && !used.has(k))) rest.push(take({ label: m.label, keys: m.keys })); });
  Object.values(byKey).forEach((f) => {
    if (used.has(f.key) || HIDDEN.has(f.key) || f.key.startsWith("_") || (prop && AREA_HIDDEN.has(f.key))) return;
    rest.push({ label: f.label, fields: [f] });
  });
  return { key, rest: rest.filter(Boolean) };
}

function Row({ row, reviewed, testid }) {
  const [open, setOpen] = useState(false);
  const snippets = row.fields.map((f) => f.snippet).filter(Boolean);
  const flag = !reviewed && row.fields.some((f) => f.confidence !== "alto");
  return (
    <button type="button" onClick={() => snippets.length && setOpen(!open)} data-testid={testid}
      className={`w-full text-left text-xs rounded-md px-1.5 py-1 -mx-1.5 transition-colors ${snippets.length ? "hover:bg-white/70 cursor-pointer" : "cursor-default"}`}>
      <div className="flex items-start gap-2">
        <span className="text-slate-500 shrink-0">{row.label}:</span>
        <span className="font-medium text-ec-green break-words">{row.fields.map(show).join(" · ")}</span>
        {flag && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold text-amber-800 bg-amber-100 shrink-0">Por rever</span>}
      </div>
      {open && snippets.map((s, i) => <div key={i} className="text-[11px] text-slate-400 italic mt-0.5">“{s}”</div>)}
    </button>
  );
}

export function AiPanel({ pid, doc, onChange, checks }) {
  const [all, setAll] = useState(false);
  const [editing, setEditing] = useState(false);
  const ai = doc.ai;
  if (!ai) return null;
  if (ai.error) {
    return <div className="mt-3 text-xs bg-red-50 text-red-700 rounded-lg px-3 py-2" data-testid={`ai-error-${doc.key}`}>Não foi possível ler o documento: {ai.error}</div>;
  }
  const reviewed = doc.ai_confirmed && !doc.ai_auto;
  const humanCard = isIdDoc(doc.key) && !reviewed;
  const { key, rest } = buildRows(ai);
  const prazo = doc.cpcv_deadline && (
    <div className="text-xs px-1.5 py-1 -mx-1.5" data-testid={`cpcv-prazo-${doc.key}`}>
      <span className="text-slate-500">Prazo contratual:</span> <span className="font-medium text-ec-green">{doc.cpcv_deadline.split("-").reverse().join("/")}</span>
    </div>
  );
  const clausulas = doc.clausulas || [];
  if (editing) {
    return (
      <div className="mt-3 rounded-xl border border-ec-gold/30 bg-ec-gold/5 p-3" data-testid={`ai-panel-${doc.key}`}>
        <div className="text-xs font-semibold text-ec-green">Corrigir dados lidos · {AI_DOC_LABEL[ai.doc_type] || ai.doc_type}</div>
        <AiCorrect pid={pid} doc={doc} onCancel={() => setEditing(false)} onDone={() => { setEditing(false); onChange(); }} />
      </div>
    );
  }
  const status = doc.ai_auto
    ? <span className="text-[11px] text-green-700 font-semibold shrink-0" data-testid={`auto-verified-${doc.key}`}>Verificado automaticamente</span>
    : reviewed
      ? <span className="text-[11px] text-green-700 font-medium shrink-0" data-testid={`reviewed-${doc.key}`}>Revisto por {niceName(doc.ai_confirmed_by)} · {fmtDate(doc.ai_confirmed_at)}</span>
      : <span className="text-[11px] text-amber-700 font-medium shrink-0" data-testid={`to-review-${doc.key}`}>{doc.ai_sample ? "Amostra · por rever" : humanCard ? "Cartão de cidadão: confirme com a imagem" : "Leitura automática, por rever"}</span>;
  return (
    <div className="mt-3 rounded-xl border border-ec-gold/30 bg-ec-gold/5 p-3" data-testid={`ai-panel-${doc.key}`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-ec-green">
          <FileText size={13} className="text-ec-gold" /> Dados do documento · {AI_DOC_LABEL[ai.doc_type] || ai.doc_type}
        </div>
        {status}
      </div>
      {checks}
      <div className={humanCard ? "flex flex-col md:flex-row gap-3" : ""}>
      {humanCard && <DocImage doc={doc} />}
      <div className="flex-1 min-w-0">
      {key.length === 0 && rest.length === 0 ? <p className="text-xs text-slate-500 mt-2">Sem campos extraídos.</p> : (
        <div className="mt-2 space-y-0.5" data-testid={`ai-key-fields-${doc.key}`}>
          {key.map((row, i) => <Row key={i} row={row} reviewed={reviewed} testid={`ai-row-${doc.key}-${i}`} />)}
          {prazo}
          {clausulas.length > 0 && (
            <div className="mt-2 rounded-lg bg-white/70 border border-slate-100 p-2.5" data-testid={`clausulas-${doc.key}`}>
              <div className="text-[11px] font-semibold text-ec-green mb-1">A ter em conta ({clausulas.length})</div>
              {clausulas.map((c, i) => (
                <div key={i} className="text-[11px] text-slate-600 py-0.5">
                  <b>Cláusula {c.numero || "—"}</b> · {c.texto} <span className="text-amber-700 font-medium">· Confirmar com o jurista</span>
                </div>
              ))}
            </div>
          )}
          {rest.length > 0 && (
            <button type="button" onClick={() => setAll(!all)} data-testid={`ai-show-all-${doc.key}`}
              className="inline-flex items-center gap-1 text-xs text-ec-green underline-offset-2 hover:underline mt-1">
              <ChevronDown size={13} className={`transition-transform ${all ? "rotate-180" : ""}`} /> {all ? "Esconder" : "Ver"} todos os dados lidos ({rest.length})
            </button>
          )}
          {all && <div className="pt-1 border-t border-ec-gold/20 mt-1" data-testid={`ai-rest-${doc.key}`}>
            {rest.map((row, i) => <Row key={i} row={row} reviewed={reviewed} testid={`ai-rest-row-${doc.key}-${i}`} />)}
          </div>}
        </div>
      )}
      </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {(ai.fields || []).length > 0 && (
          <button onClick={() => setEditing(true)} data-testid={`correct-ai-${doc.key}`}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-white transition-colors">Corrigir</button>
        )}
      </div>
    </div>
  );
}
